import { useEffect, useRef } from "react";

// Spring-physics trails that chase the pointer, drawn on a full-screen canvas.
// Each trail is a chain of nodes: the head springs toward the pointer and every
// following node springs toward the one ahead of it, so the lines whip and settle.
const CONFIG = {
  trails: 20,
  nodes: 16,
  friction: 0.5,
  dampening: 0.25,
  tension: 0.98,
  lineWidth: 1,
  // Hue drifts within the pink range so it stays on-palette (330–350).
  hueCenter: 340,
  hueRange: 10,
  saturation: 78,
  lightness: 58,
  alpha: 0.28,
  tailFade: 2.4, // >1 fades the tail out faster; 0 = no fade
  idleMs: 1600, // stop animating this long after the pointer rests
};

const makeTrail = (x, y, i) => {
  const spring = 0.4 + (i / CONFIG.trails) * 0.025;
  const friction = CONFIG.friction + Math.random() * 0.01 - 0.002;
  const nodes = Array.from({ length: CONFIG.nodes }, () => ({ x, y, vx: 0, vy: 0 }));
  return { spring, friction, nodes };
};

const stepTrail = (trail, target) => {
  let spring = trail.spring;
  let prev = target;
  for (let i = 0; i < trail.nodes.length; i++) {
    const n = trail.nodes[i];
    n.vx += (prev.x - n.x) * spring;
    n.vy += (prev.y - n.y) * spring;
    if (i > 0) {
      const ahead = trail.nodes[i - 1];
      n.vx += ahead.vx * CONFIG.dampening;
      n.vy += ahead.vy * CONFIG.dampening;
    }
    n.vx *= trail.friction;
    n.vy *= trail.friction;
    n.x += n.vx;
    n.y += n.vy;
    spring *= CONFIG.tension;
    prev = n;
  }
};

// Draws each segment separately so opacity can fall off toward the tail.
const drawTrail = (ctx, trail, color) => {
  const { nodes } = trail;
  const last = nodes.length - 1;
  let fromX = nodes[0].x;
  let fromY = nodes[0].y;
  for (let i = 1; i < last; i++) {
    const a = nodes[i];
    const b = nodes[i + 1];
    // Quadratic curves through midpoints keep the line smooth.
    const toX = i === last - 1 ? b.x : (a.x + b.x) / 2;
    const toY = i === last - 1 ? b.y : (a.y + b.y) / 2;
    const fade = Math.pow(1 - i / last, CONFIG.tailFade);
    ctx.strokeStyle = `${color}${(CONFIG.alpha * fade).toFixed(3)})`;
    ctx.beginPath();
    ctx.moveTo(fromX, fromY);
    ctx.quadraticCurveTo(a.x, a.y, toX, toY);
    ctx.stroke();
    fromX = toX;
    fromY = toY;
  }
};

const Cursor = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mq.matches) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pointer = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    let trails = [];
    let frame = null;
    let lastMove = 0;
    let phase = Math.random() * Math.PI * 2;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const render = (now) => {
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      phase += 0.0015;
      const hue = CONFIG.hueCenter + Math.sin(phase * 10) * CONFIG.hueRange;
      const color = `hsla(${hue}, ${CONFIG.saturation}%, ${CONFIG.lightness}%, `;
      ctx.lineWidth = CONFIG.lineWidth;
      ctx.lineCap = "round";
      ctx.globalCompositeOperation = "source-over";

      for (const trail of trails) {
        stepTrail(trail, pointer);
        drawTrail(ctx, trail, color);
      }

      if (now - lastMove < CONFIG.idleMs) {
        frame = requestAnimationFrame(render);
      } else {
        ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
        frame = null;
      }
    };

    const wake = () => {
      lastMove = performance.now();
      if (frame === null) frame = requestAnimationFrame(render);
    };

    const setPointer = (x, y) => {
      pointer.x = x;
      pointer.y = y;
      // Spawn trails at the first known position so they don't fly in from the corner.
      if (trails.length === 0) {
        trails = Array.from({ length: CONFIG.trails }, (_, i) => makeTrail(x, y, i));
      }
      wake();
    };

    const onMouseMove = (e) => setPointer(e.clientX, e.clientY);
    const onTouch = (e) => {
      const t = e.touches[0];
      if (t) setPointer(t.clientX, t.clientY);
    };

    resize();
    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", onMouseMove, { passive: true });
    window.addEventListener("touchstart", onTouch, { passive: true });
    window.addEventListener("touchmove", onTouch, { passive: true });

    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("touchstart", onTouch);
      window.removeEventListener("touchmove", onTouch);
    };
  }, []);

  return <canvas ref={canvasRef} aria-hidden="true" className="pointer-events-none fixed inset-0 z-[9999]" />;
};

export default Cursor;
